import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const MissionEditor = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const [missionInfo, setMissionInfo] = useState({
    title: '',
    subtitle: '',
    description: '',
    mainContent: '',
    tripDetails: {
      duration: '',
      location: '',
      dates: '',
      activities: []
    },
    goals: [],
    contactInfo: {
      email: '',
      phone: ''
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Temporary input fields for arrays
  const [newActivity, setNewActivity] = useState('');
  const [newGoal, setNewGoal] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchMissionInfo();
  }, []);

  const fetchMissionInfo = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiUrl}/mission-info`);
      if (response.ok) {
        const data = await response.json();
        setMissionInfo(data);
      }
    } catch (error) {
      console.error('Error fetching mission info:', error);
      setError('Failed to load mission information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMissionInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTripDetailChange = (field, value) => {
    setMissionInfo(prev => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        [field]: value
      }
    }));
  };

  const handleContactChange = (field, value) => {
    setMissionInfo(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [field]: value
      }
    }));
  };

  const addActivity = () => {
    if (newActivity.trim()) {
      setMissionInfo(prev => ({
        ...prev,
        tripDetails: {
          ...prev.tripDetails,
          activities: [...(prev.tripDetails.activities || []), newActivity.trim()]
        }
      }));
      setNewActivity('');
    }
  };

  const removeActivity = (index) => {
    setMissionInfo(prev => ({
      ...prev,
      tripDetails: {
        ...prev.tripDetails,
        activities: prev.tripDetails.activities.filter((_, i) => i !== index)
      }
    }));
  };

  const addGoal = () => {
    if (newGoal.trim()) {
      setMissionInfo(prev => ({
        ...prev,
        goals: [...(prev.goals || []), newGoal.trim()]
      }));
      setNewGoal('');
    }
  };

  const removeGoal = (index) => {
    setMissionInfo(prev => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage('');
    setError('');

    try {
      const response = await makeAuthenticatedRequest(`${apiUrl}/mission-info`, {
        method: 'PUT',
        body: JSON.stringify(missionInfo)
      });

      if (response.ok) {
        const data = await response.json();
        setMissionInfo(data);
        setMessage('Mission information updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update mission information');
      }
    } catch (error) {
      console.error('Error updating mission info:', error);
      setError('Failed to update mission information');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Edit Mission Information</h2>

      {message && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg">
          {message}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Basic Information */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title
          </label>
          <input
            type="text"
            name="title"
            value={missionInfo.title}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Costa Rica Missions 2025-2026"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subtitle
          </label>
          <input
            type="text"
            name="subtitle"
            value={missionInfo.subtitle}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Serving Communities with Love and Faith"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Short Description
          </label>
          <textarea
            name="description"
            value={missionInfo.description}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Brief overview that appears at the top..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Main Content
          </label>
          <textarea
            name="mainContent"
            value={missionInfo.mainContent}
            onChange={handleInputChange}
            rows="8"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Detailed information about the mission... (Use double line breaks for paragraphs)"
          />
        </div>

        {/* Trip Details */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Trip Details</h3>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration
              </label>
              <input
                type="text"
                value={missionInfo.tripDetails?.duration || ''}
                onChange={(e) => handleTripDetailChange('duration', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 2 weeks"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dates
              </label>
              <input
                type="text"
                value={missionInfo.tripDetails?.dates || ''}
                onChange={(e) => handleTripDetailChange('dates', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Summer 2025"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={missionInfo.tripDetails?.location || ''}
              onChange={(e) => handleTripDetailChange('location', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Various communities throughout Costa Rica"
            />
          </div>

          {/* Activities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activities
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newActivity}
                onChange={(e) => setNewActivity(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addActivity())}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add an activity..."
              />
              <button
                type="button"
                onClick={addActivity}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <ul className="space-y-2">
              {(missionInfo.tripDetails?.activities || []).map((activity, index) => (
                <li key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                  <span className="text-gray-700">{activity}</span>
                  <button
                    type="button"
                    onClick={() => removeActivity(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Goals */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Mission Goals</h3>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newGoal}
              onChange={(e) => setNewGoal(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGoal())}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add a goal..."
            />
            <button
              type="button"
              onClick={addGoal}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>
          <ul className="space-y-2">
            {(missionInfo.goals || []).map((goal, index) => (
              <li key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                <span className="text-gray-700">{goal}</span>
                <button
                  type="button"
                  onClick={() => removeGoal(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={missionInfo.contactInfo?.email || ''}
                onChange={(e) => handleContactChange('email', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="contact@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={missionInfo.contactInfo?.phone || ''}
                onChange={(e) => handleContactChange('phone', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="border-t pt-6">
          <button
            type="submit"
            disabled={isSaving}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Mission Information'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MissionEditor;
